import { X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import ReactDOM from "react-dom";

import { useDispatch } from "react-redux";
import { closeRightPanel } from "../redux/reducers/uiSlice";
import { usePosterReducers } from "../redux/store/useGetData";
import { textFormatter } from "../utils/textFormatter";

type RightSidePanelProps = {
  name?: string;
  title?: ReactNode;
  children?: ReactNode | ((panelData: unknown) => ReactNode);
  className?: string;
  bodyClassName?: string;
  showOverlay?: boolean;
  closeOnOverlayClick?: boolean;
};

export default function RightSidePanel({
  name,
  title,
  children,
  className = "",
  bodyClassName = "",
  showOverlay = true,
  closeOnOverlayClick = true,
}: RightSidePanelProps) {
  const dispatch = useDispatch();

  const { ui: uiState } = usePosterReducers();
  const rightPanel = uiState?.rightPanel;

  const isOpen = rightPanel?.status === "show" && (!name || rightPanel?.name === name);

  // Controls whether the component exists in the DOM
  const [isMounted, setIsMounted] = useState(isOpen);

  // Controls the actual CSS animation
  const [isVisible, setIsVisible] = useState(false);

  /**
   * Handle open / close animation
   *
   * OPEN:
   * 1. Mount component
   * 2. Start from translate-x-full
   * 3. Next frame -> translate-x-0
   *
   * CLOSE:
   * 1. translate-x-0 -> translate-x-full
   * 2. Wait for animation
   * 3. Remove from DOM
   */
  useEffect(() => {
    let animationFrame1: number | undefined;
    let animationFrame2: number | undefined;
    let closeTimer: number | undefined;

    if (isOpen) {
      // Mount first
      setIsMounted(true);

      // Wait for DOM to render in closed position
      animationFrame1 = window.requestAnimationFrame(() => {
        animationFrame2 = window.requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      // Start close animation
      setIsVisible(false);

      // Remove after animation
      closeTimer = window.setTimeout(() => {
        setIsMounted(false);
      }, 300);
    }

    return () => {
      if (animationFrame1) {
        window.cancelAnimationFrame(animationFrame1);
      }

      if (animationFrame2) {
        window.cancelAnimationFrame(animationFrame2);
      }

      if (closeTimer) {
        window.clearTimeout(closeTimer);
      }
    };
  }, [isOpen]);

  /**
   * Close panel with Escape key
   */
  useEffect(() => {
    if (!isOpen) return;

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dispatch(closeRightPanel());
      }
    };

    document.addEventListener("keydown", onEscape);

    return () => {
      document.removeEventListener("keydown", onEscape);
    };
  }, [dispatch, isOpen]);

  /**
   * Action button
   */
  const handleActionButtonClick = () => {
    if (!rightPanel?.actionButton) return;

    if (rightPanel.actionButton.href) {
      window.location.href = rightPanel.actionButton.href;
      return;
    }

    rightPanel.actionButton.onClick?.();
  };

  /**
   * Don't render anything when completely closed
   */
  if (!isMounted || !rightPanel) return null;

  const resolvedTitle = title ?? rightPanel.title;

  const panelChildren =
    children ?? rightPanel.children ?? ((panelData: unknown) => (panelData ? null : null));

  /**
   * Default row details when no custom children are provided
   */
  const getDefaultRowDetails = (row: Record<string, any>) => {
    const definition = [
      {
        key: "channelCode",
        label: "Licence no.",
      },
      {
        key: "type",
        label: "Licence category",
      },
      {
        key: "licenceValidity",
        label: "Valid till",
      },
      {
        key: "area",
        label: "Area",
      },
      {
        key: "users",
        label: "Users",
      },
      {
        key: "lastUpdated",
        label: "Last updated",
      },
    ];

    return definition
      .filter(({ key }) => row[key] !== undefined && row[key] !== null && row[key] !== "")
      .map(({ key, label }) => ({
        label,
        value: row[key],
      }));
  };

  /**
   * Resolve panel content
   */
  const resolvedContent =
    typeof panelChildren === "function"
      ? panelChildren(rightPanel.data)
      : (panelChildren ??
        (rightPanel.data && typeof rightPanel.data === "object" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {getDefaultRowDetails(rightPanel.data as Record<string, any>).map((item, index) => (
                <div
                  key={`${item.label}-${index}`}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="text-xs font-medium text-gray-500">{item.label}</div>

                  <div className="mt-1 text-sm font-semibold text-gray-800">
                    {item.value ?? "-"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null));

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 z-999 flex justify-end ${
        isVisible ? "pointer-events-auto" : "pointer-events-none"
      }`}
      aria-hidden={!isVisible}
    >
      {/* Overlay */}
      {showOverlay ? (
        <button
          type="button"
          aria-label="Close right panel"
          className={`absolute inset-0 bg-black/25 backdrop-blur-[1px] transition-opacity duration-300 ease-out ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => {
            if (closeOnOverlayClick) {
              dispatch(closeRightPanel());
            }
          }}
        />
      ) : null}

      {/* Right Side Panel */}
      <aside
        className={`relative h-full w-full max-w-xl bg-white shadow-2xl ring-1 ring-black/5 transform-gpu transition-transform duration-300 ease-out ${
          isVisible ? "translate-x-0" : "translate-x-full"
        } ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof resolvedTitle === "string" ? resolvedTitle : "Right side panel"}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-theme-text">
            {typeof resolvedTitle === "string" ? textFormatter(resolvedTitle) : resolvedTitle}
          </h2>

          <button
            type="button"
            onClick={() => dispatch(closeRightPanel())}
            aria-label="Close"
            className="cursor-pointer text-gray-400 transition-all duration-200 ease-in-out hover:scale-110 hover:rotate-90 hover:text-theme-danger active:scale-95"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className={`h-[calc(100%-65px)] overflow-y-auto thin-scrollbar p-5 ${bodyClassName}`}>
          {resolvedContent}

          {/* Action Button */}
          {/* {rightPanel.actionButton && (
            <div className="mt-5 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={handleActionButtonClick}
                disabled={rightPanel.actionButton.disabled}
                className={`w-full rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
                  rightPanel.actionButton.variant === "secondary"
                    ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    : "bg-theme-primary text-white hover:bg-theme-primary-hover"
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {rightPanel.actionButton.label}
              </button>
            </div>
          )} */}
        </div>
      </aside>
    </div>,
    document.body
  );
}
